import {createFileRoute, Outlet} from "@tanstack/react-router";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useRef, useState} from "react";
import {
    Button,
    DialogActions,
    DialogContent,
    IconButton,
    Modal,
    ModalClose,
    ModalDialog,
    Sheet,
    Stack,
    Typography,
    useTheme
} from "@mui/joy";
import {faEraser} from "@fortawesome/pro-regular-svg-icons";
import {useAutologin} from "../auth/authenticator.tsx";
import {NaviProvider} from "../navi/Navi.tsx";
import {canvasToBlob, PhotoZoomContext} from "../photo.tsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {SetlistFeedProvider} from "../rpc/setlist.tsx";
import {ActionButton} from "../components/ActionButton.tsx";
import SignatureCanvas from "react-signature-canvas";
import {VenueOverviewProvider} from "../venue/overview.tsx";
import {RefreshProvider} from "../util/tqrefresh.tsx";
import {confirmModalActionButtonSx, ConfirmModalContext, ConfirmModalProps} from "../confirmModalProps.tsx";
import {DispatchMeshManager} from "../dispatch/dispatchMeshManager.tsx";
import {DriverRelay} from "../driver/DriverRelay.tsx";

function AuthedHarness() {
    const {t} = useTranslation();
    const theme = useTheme();
    useAutologin();

    const [zoomPhotoUrl, setZoomPhotoUrl] = useState<string | null>(null);

    const confirmSignatureRef = useRef<SignatureCanvas>(null);
    const [confirmModalProps, setConfirmModalProps]
        = useState<ConfirmModalProps | null>(null);
    const confirmModalContextState = useMemo(() => {
        return {
            open: (props: ConfirmModalProps) => {
                setConfirmModalProps(props);
            }
        }
    }, []);
    const fireConfirmation = useCallback(() => {
        const sigBlob$ = confirmModalProps?.signatureWanted
        && confirmSignatureRef.current !== null
        && !confirmSignatureRef.current.isEmpty()
            ? canvasToBlob(confirmSignatureRef.current.getTrimmedCanvas())
            : Promise.resolve(null);
        if (confirmModalProps?.onYes) {
            confirmModalProps.onYes(sigBlob$);
        }
        setConfirmModalProps(null);
        return Promise.resolve();
    }, [confirmModalProps]);

    return <SetlistFeedProvider>
        <DispatchMeshManager>
            <VenueOverviewProvider>
                <DriverRelay>
                    <NaviProvider>
                        <PhotoZoomContext value={{url: zoomPhotoUrl, setUrl: setZoomPhotoUrl}}>
                            <ConfirmModalContext value={confirmModalContextState}>
                                <RefreshProvider>

                                    <Outlet/>

                                    <Modal open={!!zoomPhotoUrl}
                                           onClose={() => {
                                               setZoomPhotoUrl(null);
                                           }} sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                                        <Sheet sx={{maxHeight: "95vh", maxWidth: "95vw"}}>
                                            <ModalClose variant="soft"/>
                                            <img style={{objectFit: "scale-down", maxHeight: "90vh", maxWidth: "90vw"}}
                                                 src={zoomPhotoUrl!}/>
                                        </Sheet>
                                    </Modal>
                                    <Modal open={!!confirmModalProps}
                                           onClose={() => {
                                               setConfirmModalProps(null);
                                           }}
                                           sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                                        <ModalDialog>
                                            <DialogContent>
                                                <Typography level="body-md">{confirmModalProps?.question}</Typography>
                                                {confirmModalProps?.signatureWanted
                                                    ? <Stack direction="row" spacing={1.8} sx={{
                                                        border: 2,
                                                    }}>
                                                        <SignatureCanvas
                                                            ref={confirmSignatureRef}
                                                            penColor={theme.palette.primary[800]}
                                                            canvasProps={{width: 500, height: 200}}/>
                                                        <IconButton type="button" onClick={() => {
                                                            if (confirmSignatureRef.current) {
                                                                confirmSignatureRef.current.clear();
                                                            }
                                                        }} sx={{p: 1}} color="warning" variant="soft">
                                                            <FontAwesomeIcon icon={faEraser} size="2x"/>
                                                        </IconButton>
                                                    </Stack>
                                                    : null}
                                            </DialogContent>
                                            <DialogActions>
                                                <ActionButton color="primary" variant="solid"
                                                              sx={confirmModalActionButtonSx}
                                                              perform={fireConfirmation}>
                                                    {confirmModalProps?.yesAnswer}
                                                </ActionButton>
                                                <Button color="neutral" variant="soft"
                                                        sx={confirmModalActionButtonSx}
                                                        onClick={() => {
                                                            setConfirmModalProps(null);
                                                        }}>
                                                    {confirmModalProps?.noAnswer ?? t("cancel")}
                                                </Button>
                                            </DialogActions>
                                        </ModalDialog>
                                    </Modal>
                                </RefreshProvider>
                            </ConfirmModalContext>
                        </PhotoZoomContext>
                    </NaviProvider>
                </DriverRelay>
            </VenueOverviewProvider>
        </DispatchMeshManager>
    </SetlistFeedProvider>
}

export const Route
    = createFileRoute("/_auth")({
    component: AuthedHarness
});

